import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import api from "../api";

const Page = styled.div`
  min-height: 100vh;
  padding: 20px;
  color: #10243f;
  background:
    radial-gradient(circle at top left, rgba(14, 165, 233, 0.1), transparent 24%),
    radial-gradient(circle at top right, rgba(20, 184, 166, 0.09), transparent 26%),
    linear-gradient(180deg, #f4f8fc 0%, #eef4fb 100%);
`;

const Hero = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.25fr) minmax(300px, 0.9fr);
  gap: 16px;
  margin-bottom: 18px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const HeroCard = styled.div`
  background: linear-gradient(145deg, #11243d 0%, #19486d 52%, #0f766e 100%);
  color: #f8fafc;
  border-radius: 24px;
  padding: 24px;
  box-shadow: 0 24px 50px rgba(15, 23, 42, 0.18);
`;

const HeroBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const HeroTitle = styled.h2`
  margin: 14px 0 10px;
  font-size: clamp(1.8rem, 2.8vw, 2.3rem);
  line-height: 1.12;
`;

const HeroText = styled.p`
  margin: 0;
  color: rgba(248, 250, 252, 0.82);
  line-height: 1.65;
`;

const ScopeCard = styled.div`
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(203, 213, 225, 0.72);
  border-radius: 24px;
  padding: 22px;
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.08);
`;

const ScopeTitle = styled.div`
  font-size: 0.82rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #0f172a;
  margin-bottom: 12px;
`;

const ScopeNote = styled.div`
  color: #475569;
  line-height: 1.6;
  margin-bottom: 14px;
`;

const ScopeGrid = styled.div`
  display: grid;
  gap: 10px;
`;

const ScopeItem = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 12px 14px;
`;

const ScopeLabel = styled.div`
  color: #64748b;
  font-size: 0.78rem;
  font-weight: 700;
  margin-bottom: 4px;
`;

const ScopeValue = styled.div`
  color: #10243f;
  font-weight: 800;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;

  @media (max-width: 1180px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(203, 213, 225, 0.72);
  border-radius: 20px;
  padding: 18px;
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.08);
`;

const StatLabel = styled.div`
  color: #64748b;
  font-size: 0.8rem;
  font-weight: 700;
  margin-bottom: 8px;
`;

const StatValue = styled.div`
  color: #0f172a;
  font-size: 1.9rem;
  font-weight: 900;
`;

const StatMeta = styled.div`
  margin-top: 8px;
  color: #475569;
  font-size: 0.84rem;
  line-height: 1.45;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(203, 213, 225, 0.72);
  border-radius: 22px;
  padding: 18px;
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.08);
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: 1.15fr 1fr 1fr 1fr 1fr auto auto;
  gap: 10px;

  @media (max-width: 1280px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: grid;
  gap: 6px;
`;

const FieldLabel = styled.label`
  color: #475569;
  font-size: 0.78rem;
  font-weight: 700;
`;

const Input = styled.input`
  width: 100%;
  min-height: 46px;
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  padding: 11px 13px;
  color: #0f172a;
  background: #fff;

  &:focus {
    outline: none;
    border-color: #0f766e;
    box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.12);
  }
`;

const Select = styled.select`
  width: 100%;
  min-height: 46px;
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  padding: 11px 13px;
  color: #0f172a;
  background: #fff;

  &:focus {
    outline: none;
    border-color: #0f766e;
    box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.12);
  }
`;

const ActionWrap = styled.div`
  display: flex;
  gap: 10px;
  align-items: end;
`;

const Btn = styled.button`
  min-height: 46px;
  border: none;
  border-radius: 12px;
  padding: 0 16px;
  font-weight: 800;
  cursor: pointer;
  background: ${(p) => (p.$secondary ? "#e2e8f0" : "linear-gradient(135deg, #0f766e 0%, #2563eb 100%)")};
  color: ${(p) => (p.$secondary ? "#10243f" : "#fff")};
  opacity: ${(p) => (p.disabled ? 0.5 : 1)};
  pointer-events: ${(p) => (p.disabled ? "none" : "auto")};
`;

const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  min-width: 1240px;
  border-collapse: separate;
  border-spacing: 0;

  th,
  td {
    border-bottom: 1px solid #e2e8f0;
    padding: 14px 12px;
    text-align: left;
    vertical-align: top;
  }

  th {
    position: sticky;
    top: 0;
    background: #f8fafc;
    color: #64748b;
    font-size: 0.76rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    z-index: 1;
  }
`;

const UserCell = styled.div`
  display: grid;
  gap: 4px;
`;

const UserName = styled.div`
  color: #10243f;
  font-weight: 800;
`;

const UserMeta = styled.div`
  color: #64748b;
  font-size: 0.8rem;
`;

const ResourceCell = styled.div`
  display: grid;
  gap: 4px;
`;

const ResourceType = styled.div`
  color: #10243f;
  font-weight: 700;
`;

const ResourceId = styled.div`
  color: #64748b;
  font-size: 0.8rem;
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 86px;
  padding: 7px 10px;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: ${(p) => (p.$tone === "warning" ? "#fff7ed" : "#ecfeff")};
  color: ${(p) => (p.$tone === "warning" ? "#c2410c" : "#0f766e")};
  border: 1px solid ${(p) => (p.$tone === "warning" ? "#fdba74" : "#99f6e4")};
`;

const InspectButton = styled(Btn)`
  min-height: 38px;
  padding: 0 12px;
  background: ${(p) => (p.$secondary ? "#eff6ff" : "#dbeafe")};
  color: #1d4ed8;
`;

const DetailRow = styled.tr`
  td {
    background: #f8fafc;
    padding-top: 0;
  }
`;

const DetailPanel = styled.div`
  margin: 0 0 14px;
  padding: 14px;
  border-radius: 16px;
  border: 1px solid #dbe7f3;
  background: #ffffff;
`;

const Pre = styled.pre`
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  background: #0f172a;
  color: #e2e8f0;
  border-radius: 14px;
  padding: 14px;
  font-size: 0.76rem;
  line-height: 1.6;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding-top: 16px;
  flex-wrap: wrap;
`;

const FooterText = styled.div`
  color: #64748b;
  font-size: 0.84rem;
  line-height: 1.6;
`;

const Pager = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PageBadge = styled.div`
  min-height: 42px;
  display: inline-flex;
  align-items: center;
  padding: 0 14px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #334155;
  font-weight: 700;
`;

const EmptyState = styled.div`
  padding: 34px 18px;
  text-align: center;
  color: #64748b;
  line-height: 1.7;
`;

const Skeleton = styled.div`
  height: ${(p) => p.$h || 48}px;
  border-radius: 14px;
  background: linear-gradient(90deg, #e2e8f0 25%, #f8fafc 50%, #e2e8f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.2s infinite;

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const ErrorBox = styled.div`
  margin-bottom: 14px;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  font-weight: 700;
  font-size: 0.88rem;
`;

function formatDateTime(value) {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatCount(value) {
  return Number(value || 0).toLocaleString("en-IN");
}

export default function DetailedAuditLogs() {
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0, per_page: 20 });
  const [filters, setFilters] = useState({
    user_id: "",
    action: "",
    resource_type: "",
    from_date: "",
    to_date: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({
    user_id: "",
    action: "",
    resource_type: "",
    from_date: "",
    to_date: "",
  });

  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.get("/admin/users", { params: { page: 1, limit: 100 } });
      setUsers(res?.data?.data || []);
    } catch {
      setUsers([]);
    }
  }, []);

  const fetchLogs = useCallback(
    async (page = 1, activeFilters = appliedFilters) => {
      setLoading(true);
      setError("");
      try {
        const params = { page, per_page: pagination.per_page };
        if (activeFilters.user_id) params.user_id = Number(activeFilters.user_id);
        if (activeFilters.action.trim()) params.action = activeFilters.action.trim();
        if (activeFilters.resource_type.trim()) params.resource_type = activeFilters.resource_type.trim();
        if (activeFilters.from_date) params.from_date = activeFilters.from_date;
        if (activeFilters.to_date) params.to_date = activeFilters.to_date;

        const res = await api.get("/admin/audit-logs", { params });
        setLogs(res?.data?.data || []);
        setPagination((prev) => ({ ...prev, ...(res?.data?.pagination || {}) }));
      } catch (err) {
        setError(err?.response?.data?.message || "Failed to load audit logs.");
      } finally {
        setLoading(false);
      }
    },
    [appliedFilters, pagination.per_page]
  );

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchLogs(1, appliedFilters);
  }, [appliedFilters, fetchLogs]);

  const stats = useMemo(() => {
    const successCount = logs.filter((log) => log.status !== "warning").length;
    const warningCount = logs.filter((log) => log.status === "warning").length;
    const uniqueUsers = new Set(logs.map((log) => log.user_id).filter(Boolean)).size;
    const actionTypes = new Set(logs.map((log) => log.action).filter(Boolean)).size;
    return { successCount, warningCount, uniqueUsers, actionTypes };
  }, [logs]);

  const applyFilters = () => {
    setAppliedFilters({ ...filters });
    setExpanded({});
  };

  const resetFilters = () => {
    const cleared = {
      user_id: "",
      action: "",
      resource_type: "",
      from_date: "",
      to_date: "",
    };
    setFilters(cleared);
    setAppliedFilters(cleared);
    setExpanded({});
  };

  return (
    <Page>
      <Hero>
        <HeroCard>
          <HeroBadge>Org Audit Trail</HeroBadge>
          <HeroTitle>Detailed audit logs, filtered to the current organization only.</HeroTitle>
          <HeroText>
            Every row below is scoped to your organization so admins can inspect actions, resource changes, and user behavior without
            seeing any other organization&apos;s history.
          </HeroText>
        </HeroCard>

        <ScopeCard>
          <ScopeTitle>Visibility Rules</ScopeTitle>
          <ScopeNote>
            The audit table, totals, filters, and pagination are all computed from the current organization&apos;s logs only.
          </ScopeNote>
          <ScopeGrid>
            <ScopeItem>
              <ScopeLabel>Users Included</ScopeLabel>
              <ScopeValue>Staff and admins mapped to this organization</ScopeValue>
            </ScopeItem>
            <ScopeItem>
              <ScopeLabel>Resources Included</ScopeLabel>
              <ScopeValue>Only organization-scoped audited entities</ScopeValue>
            </ScopeItem>
            <ScopeItem>
              <ScopeLabel>Cross-Org Visibility</ScopeLabel>
              <ScopeValue>Blocked from this screen</ScopeValue>
            </ScopeItem>
          </ScopeGrid>
        </ScopeCard>
      </Hero>

      {error ? <ErrorBox>{error}</ErrorBox> : null}

      <StatsGrid>
        <StatCard>
          <StatLabel>Visible Rows</StatLabel>
          <StatValue>{formatCount(pagination.total)}</StatValue>
          <StatMeta>Total matching audit entries for this organization under the current filters.</StatMeta>
        </StatCard>
        <StatCard>
          <StatLabel>Success Signals</StatLabel>
          <StatValue>{formatCount(stats.successCount)}</StatValue>
          <StatMeta>Rows currently marked as successful or normal activity in this page slice.</StatMeta>
        </StatCard>
        <StatCard>
          <StatLabel>Attention Signals</StatLabel>
          <StatValue>{formatCount(stats.warningCount)}</StatValue>
          <StatMeta>Deletes, failures, errors, and other warning-pattern events in view.</StatMeta>
        </StatCard>
        <StatCard>
          <StatLabel>Users / Actions</StatLabel>
          <StatValue style={{ fontSize: "1.1rem", lineHeight: 1.25 }}>
            {formatCount(stats.uniqueUsers)} / {formatCount(stats.actionTypes)}
          </StatValue>
          <StatMeta>Distinct users and action types represented in the current result page.</StatMeta>
        </StatCard>
      </StatsGrid>

      <Card style={{ marginBottom: 18 }}>
        <FilterGrid>
          <Field>
            <FieldLabel>User</FieldLabel>
            <Select value={filters.user_id} onChange={(e) => setFilters((f) => ({ ...f, user_id: e.target.value }))}>
              <option value="">All organization users</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field>
            <FieldLabel>Action</FieldLabel>
            <Input
              placeholder="create_branch"
              value={filters.action}
              onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value }))}
            />
          </Field>

          <Field>
            <FieldLabel>Resource Type</FieldLabel>
            <Input
              placeholder="user, design, branch"
              value={filters.resource_type}
              onChange={(e) => setFilters((f) => ({ ...f, resource_type: e.target.value }))}
            />
          </Field>

          <Field>
            <FieldLabel>From Date</FieldLabel>
            <Input type="date" value={filters.from_date} onChange={(e) => setFilters((f) => ({ ...f, from_date: e.target.value }))} />
          </Field>

          <Field>
            <FieldLabel>To Date</FieldLabel>
            <Input type="date" value={filters.to_date} onChange={(e) => setFilters((f) => ({ ...f, to_date: e.target.value }))} />
          </Field>

          <ActionWrap>
            <Btn onClick={applyFilters}>Apply</Btn>
            <Btn $secondary onClick={resetFilters}>Clear</Btn>
          </ActionWrap>
        </FilterGrid>
      </Card>

      <Card>
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>User</th>
                <th>Action</th>
                <th>Resource</th>
                <th>Status</th>
                <th>Time</th>
                <th>IP Address</th>
                <th>Inspect</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 8 }).map((_, idx) => (
                  <tr key={idx}>
                    <td colSpan={7}>
                      <Skeleton />
                    </td>
                  </tr>
                ))
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <EmptyState>
                      No audit logs matched the current filters for this organization.
                      <br />
                      Try widening the date range or clearing the action filter.
                    </EmptyState>
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <React.Fragment key={log.id}>
                    <tr>
                      <td>
                        <UserCell>
                          <UserName>{log.user_name || "System"}</UserName>
                          <UserMeta>User ID: {log.user_id || "-"}</UserMeta>
                        </UserCell>
                      </td>
                      <td>{log.action || "-"}</td>
                      <td>
                        <ResourceCell>
                          <ResourceType>{log.resource_type || "General"}</ResourceType>
                          <ResourceId>{log.resource_id ? `Resource #${log.resource_id}` : "No resource id"}</ResourceId>
                        </ResourceCell>
                      </td>
                      <td>
                        <Pill $tone={log.status}>{log.status || "success"}</Pill>
                      </td>
                      <td>{formatDateTime(log.created_at)}</td>
                      <td>{log.ip_address || "-"}</td>
                      <td>
                        <InspectButton $secondary onClick={() => setExpanded((prev) => ({ ...prev, [log.id]: !prev[log.id] }))}>
                          {expanded[log.id] ? "Hide" : "Inspect"}
                        </InspectButton>
                      </td>
                    </tr>
                    {expanded[log.id] ? (
                      <DetailRow>
                        <td colSpan={7}>
                          <DetailPanel>
                            <Pre>{JSON.stringify(log.details || {}, null, 2)}</Pre>
                          </DetailPanel>
                        </td>
                      </DetailRow>
                    ) : null}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </Table>
        </TableWrap>

        <Footer>
          <FooterText>
            Showing organization-scoped audit records only. Other organizations&apos; logs are hidden from this view even when they share
            the same database.
          </FooterText>
          <Pager>
            <Btn $secondary disabled={(pagination.page || 1) <= 1} onClick={() => fetchLogs((pagination.page || 1) - 1)}>
              Prev
            </Btn>
            <PageBadge>
              Page {pagination.page || 1} / {pagination.pages || 1}
            </PageBadge>
            <Btn
              $secondary
              disabled={(pagination.page || 1) >= (pagination.pages || 1)}
              onClick={() => fetchLogs((pagination.page || 1) + 1)}
            >
              Next
            </Btn>
          </Pager>
        </Footer>
      </Card>
    </Page>
  );
}
